import i18next from 'i18next';
import { localize } from '@/utils/approval/localize';
import type {
  EasVoucherAttributes,
  SaVoucherItem,
  SaVoucherLine,
} from '@/types/approval';

export const SA_VOUCHER_APPROVAL_TYPE = 99;

export const isSaVoucherApprovalType = (apvType: number): boolean =>
  apvType === SA_VOUCHER_APPROVAL_TYPE;

/** SAP → 작성 화면 진입 쿼리 키 — 레거시 `/intranet/approval/create?apv_type=99&Ep=` 와 동일 */
export const SA_VOUCHER_ENTRY_QUERY_KEY = 'Ep';

/** 진입 쿼리 → SAP 묶음번호. 반복 키는 첫 값, 공백만 있으면 없음 */
export const getEntrySapDocumentNo = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[SA_VOUCHER_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

/** 작성 폼(RHF saVoucher) 스냅샷 — draft-source 응답 또는 편집 저장값 */
export type SaVoucherSnapshot = {
  sapDocumentNo: string;
  zSum: string | null;
  vouchers: SaVoucherLine[];
};

const text = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/**
 * 단건조회 attributes → 스냅샷. sapDocumentNo 가 없으면 null.
 * vouchers 는 V2 작성 문서만 있음 — 레거시 문서는 null 이라 호출부가 content HTML 로 대체
 */
export const parseSaVoucherAttributes = (
  attributes: unknown,
):
  | (SaVoucherSnapshot & { hasVoucherSnapshot: boolean; content: string })
  | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  const sapDocumentNo = text(a.sapDocumentNo).trim();
  if (!sapDocumentNo) return null;
  const hasVoucherSnapshot = Array.isArray(a.vouchers);
  return {
    sapDocumentNo,
    zSum: text(a.zSum) || null,
    vouchers: hasVoucherSnapshot ? (a.vouchers as SaVoucherLine[]) : [],
    hasVoucherSnapshot,
    content: text(a.content),
  };
};

type SaVoucherItemColumn = {
  key: keyof SaVoucherItem;
  label: string;
  align: 'left' | 'center' | 'right';
};

/** 전표 상세(G/L) 열 — 순서는 레거시 상세 표 그대로. 화면·저장 HTML·레거시 파싱이 같은 순서를 씀 */
export const SA_VOUCHER_ITEM_COLUMNS: SaVoucherItemColumn[] = [
  { key: 'debitCreditName', label: '차/대', align: 'center' },
  { key: 'accountCode', label: 'G/L 계정', align: 'center' },
  { key: 'accountName', label: 'G/L 계정명', align: 'left' },
  { key: 'taxCode', label: 'Tax', align: 'center' },
  { key: 'amount', label: '금액(입력)', align: 'right' },
  { key: 'localAmount', label: '금액(원화환산)', align: 'right' },
  { key: 'itemText', label: '텍스트', align: 'left' },
  { key: 'partnerCode', label: '거래처코드', align: 'center' },
  { key: 'partnerName', label: '거래처명', align: 'center' },
  { key: 'costCenterCode', label: '코스트센터', align: 'center' },
  { key: 'costCenterName', label: '코스트센터명', align: 'center' },
  { key: 'paymentMethod', label: '지급방법', align: 'left' },
  { key: 'baselineDate', label: '가산일', align: 'center' },
];

const LEGACY_VOUCHER_ROW_SELECTOR = 'tr[id^="voucher-row-"]';
const LEGACY_VOUCHER_ROW_ID_PREFIX = 'voucher-row-';
const LEGACY_VOUCHER_DETAIL_ID_PREFIX = 'voucher-detail-';
/** 레거시 전표 표 열 — 순서는 행 id 가 있는 최신 레거시 표 그대로. 예전 표는 머리글 글자로 위치를 찾음 */
const LEGACY_VOUCHER_COLUMNS = [
  '순번',
  '전표유형',
  '전기일',
  '전표번호',
  '통화',
  '입력금액',
  '원화금액',
  '적요',
  '사용일자',
  '사용처',
  '사용처주소',
  '라인적요',
] as const;
const LEGACY_COMPANY_SELECTOR = '#sap_company_text';

/**
 * 레거시 「보기」 칸 onclick 에서 SAP 주소와 폼 값만 꺼내 V2 와 같은 링크로 — 스크립트는 실행하지 않음.
 * 값은 레거시가 보내던 그대로(base64) 둔다
 */
const parseLegacySapLink = (
  cell: HTMLTableCellElement | undefined,
): string | null => {
  const onclick = cell?.querySelector('a[onclick]')?.getAttribute('onclick');
  const action = onclick?.match(/'action'\s*:\s*'([^']+)'/)?.[1];
  if (!onclick || !action) return null;
  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return null;
  }
  // 저장 HTML 의 주소를 그대로 열므로 사내 SAP(*.fnf.co.kr) 만 허용
  if (url.protocol !== 'https:' || !url.hostname.endsWith('.fnf.co.kr')) {
    return null;
  }
  for (const [, name, value] of onclick.matchAll(
    /'name'\s*:\s*'([^']+)'\s*,\s*'value'\s*:\s*'([^']*)'/g,
  )) {
    url.searchParams.set(name, value);
  }
  return url.toString();
};

/**
 * 레거시 작성 문서의 attributes.content(전표 표 HTML) → 전표 라인. 열 순서는 레거시 폼 그대로.
 * HTML 을 그대로 렌더하지 않고 텍스트와 SAP 링크 값만 꺼냄 — 레거시 표에 인라인 onclick 이 들어 있음
 */
export const parseLegacyVoucherContent = (
  html: string,
): { companyName: string | null; vouchers: SaVoucherLine[] } => {
  if (!html || typeof DOMParser === 'undefined') {
    return { companyName: null, vouchers: [] };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const cellText = (cells: HTMLTableCellElement[], index: number) =>
    cells[index]?.textContent?.trim() || null;
  // 상세 행이 아예 없으면 null(상세 없는 표), "상세 정보가 없습니다." 한 칸짜리 행만 있으면 빈 배열
  const parseItems = (row: HTMLTableRowElement): SaVoucherItem[] | null => {
    const seq = row.id.slice(LEGACY_VOUCHER_ROW_ID_PREFIX.length);
    const detail = doc.getElementById(
      `${LEGACY_VOUCHER_DETAIL_ID_PREFIX}${seq}`,
    );
    if (!detail) return null;
    // 바깥 표 tbody 도 조상이라 직계로 좁혀야 상세 표 머리 행이 안 섞임
    return Array.from(
      detail.querySelectorAll<HTMLTableRowElement>(
        ':scope > td > table > tbody > tr',
      ),
    )
      .map((itemRow) => Array.from(itemRow.cells))
      .filter((cells) => cells.length >= SA_VOUCHER_ITEM_COLUMNS.length)
      .map((cells) =>
        Object.fromEntries(
          SA_VOUCHER_ITEM_COLUMNS.map(({ key }, index) => [
            key,
            cellText(cells, index),
          ]),
        ),
      );
  };
  const idRows = Array.from(
    doc.querySelectorAll<HTMLTableRowElement>(LEGACY_VOUCHER_ROW_SELECTOR),
  );
  // 예전(2021~2025년 중반) 레거시 표는 전표 행에 id 가 없고 열 구성도 달라(라인적요 없음 등) 머리글로 칸을 찾음.
  // 다른 표 행이 섞이지 않게 머리글이 있는 표의 직속 행만 봄
  const headerRow = idRows.length
    ? undefined
    : Array.from(doc.querySelectorAll<HTMLTableRowElement>('tr')).find((row) =>
        Array.from(row.cells).some(
          (cell) =>
            cell.tagName === 'TH' && cell.textContent?.trim() === '전표유형',
        ),
      );
  const headerLabels = headerRow
    ? Array.from(headerRow.cells).map((cell) => cell.textContent?.trim() ?? '')
    : null;
  const headerTable = headerRow?.closest('table');
  const voucherRows =
    headerLabels && headerTable
      ? Array.from(headerTable.rows).filter(
          (row) =>
            row.cells.length === headerLabels.length &&
            !row.querySelector('th'),
        )
      : idRows;
  const columnIndex = (label: (typeof LEGACY_VOUCHER_COLUMNS)[number]) =>
    (headerLabels ?? LEGACY_VOUCHER_COLUMNS).indexOf(label);
  const vouchers = voucherRows.map((row) => {
    const cells = Array.from(row.cells);
    const col = (label: (typeof LEGACY_VOUCHER_COLUMNS)[number]) =>
      cellText(cells, columnIndex(label));
    return {
      items: parseItems(row),
      seq: col('순번'),
      documentType: col('전표유형'),
      // 레거시는 전기일 뒤에 "(요일)" 을 붙여 저장 — 표시 때 다시 붙이므로 떼어 냄
      postingDate: col('전기일')?.replace(/\s*\([^)]*\)\s*$/, '') || null,
      documentNumber: col('전표번호'),
      currency: col('통화'),
      amount: col('입력금액'),
      localAmount: col('원화금액'),
      headerText: col('적요'),
      text1: col('사용일자'),
      text2: col('사용처'),
      text3: col('사용처주소'),
      itemText: col('라인적요'),
      link: parseLegacySapLink(cells[cells.length - 1]),
    };
  });
  const companyName =
    doc.querySelector(LEGACY_COMPANY_SELECTOR)?.textContent?.trim() || null;
  return { companyName, vouchers };
};

const WEEKDAY_LOCALES: Record<string, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  zh: 'zh-CN',
};

const weekdayOf = (date: Date): string =>
  new Intl.DateTimeFormat(
    WEEKDAY_LOCALES[i18next.language?.slice(0, 2)] ?? 'ko-KR',
    { weekday: 'short' },
  ).format(date);

/** "2026.05.22" → "2026.05.22 (금)". 파싱 못 하면 원문 그대로 — 레거시 표기 */
export const formatPostingDate = (date: string | null | undefined): string => {
  const value = date?.trim() ?? '';
  const matched = value.match(/^(\d{4})\D(\d{1,2})\D(\d{1,2})$/);
  if (!matched) return value;
  const parsed = new Date(
    Number(matched[1]),
    Number(matched[2]) - 1,
    Number(matched[3]),
  );
  return Number.isNaN(parsed.getTime())
    ? value
    : `${value} (${weekdayOf(parsed)})`;
};

/** 전표 라인들의 회사명 — 묶음 하나는 한 회사라 첫 값 */
export const getVoucherCompanyName = (
  vouchers: SaVoucherLine[],
): string | null => vouchers.find((v) => v.companyName)?.companyName ?? null;

const escapeHtml = (value: string | null | undefined): string =>
  (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * attributes.content(서버 필수) — 레거시 조회 화면이 그대로 그리는 전표 표 HTML.
 * 레거시 폼과 같은 id(voucher-row-·sap_company_text)를 둬 parseLegacyVoucherContent 로도 읽힘
 */
export const buildSaVoucherContentHtml = ({
  zSum,
  vouchers,
}: SaVoucherSnapshot): string => {
  const head = [
    '순번',
    '전표유형',
    '전기일',
    '전표번호',
    '통화',
    '입력금액',
    '원화금액',
    '적요',
    '사용일자',
    '사용처',
    '사용처주소',
    '라인적요',
  ]
    .map((label) => `<th class="text-center">${localize(label)}</th>`)
    .join('');
  const itemHead = SA_VOUCHER_ITEM_COLUMNS.map(
    ({ label }) => `<th class="text-center">${localize(label)}</th>`,
  ).join('');
  const detailRow = (seq: string, items: SaVoucherItem[]) => {
    const body = items.length
      ? items
          .map(
            (item) =>
              `<tr>${SA_VOUCHER_ITEM_COLUMNS.map(
                ({ key, align }) =>
                  `<td class="text-${align}">${escapeHtml(item[key])}</td>`,
              ).join('')}</tr>`,
          )
          .join('')
      : `<tr><td colspan="${
          SA_VOUCHER_ITEM_COLUMNS.length
        }" class="text-center">${localize('상세 정보가 없습니다.')}</td></tr>`;
    // 레거시 조회 화면이 class·id 로 숨김/토글하므로 이름 유지
    return (
      `<tr id="${LEGACY_VOUCHER_DETAIL_ID_PREFIX}${seq}" class="voucher-detail-row" style="display: none;">` +
      `<td colspan="12" style="padding: 0;"><table class="table mb-0">` +
      `<thead><tr>${itemHead}</tr></thead><tbody>${body}</tbody></table></td></tr>`
    );
  };
  const rows = vouchers
    .map((v, index) => {
      const seq = escapeHtml(v.seq ?? String(index + 1));
      const detail = detailRow(seq, v.items ?? []);
      return (
        `<tr id="voucher-row-${seq}">` +
        `<td class="text-center">${seq}</td>` +
        `<td class="text-center">${escapeHtml(v.documentType)}</td>` +
        `<td class="text-center">${escapeHtml(
          formatPostingDate(v.postingDate),
        )}</td>` +
        `<td class="text-center">${escapeHtml(v.documentNumber)}</td>` +
        `<td class="text-center">${escapeHtml(v.currency)}</td>` +
        `<td class="text-right">${escapeHtml(v.amount)}</td>` +
        `<td class="text-right">${escapeHtml(v.localAmount)}</td>` +
        `<td class="text-left">${escapeHtml(v.headerText)}</td>` +
        `<td class="text-center">${escapeHtml(v.text1)}</td>` +
        `<td class="text-center">${escapeHtml(v.text2)}</td>` +
        `<td class="text-left">${escapeHtml(v.text3)}</td>` +
        `<td class="text-left">${escapeHtml(v.itemText)}</td>` +
        '</tr>' +
        detail
      );
    })
    .join('');
  return (
    '<div id="eas_voucher_content">' +
    '<div class="d-flex align-items-center justify-content-between form-group">' +
    `<div><span style="font-weight:bold;">${localize('회사명')} : </span>` +
    `<span style="font-weight:bold;" id="sap_company_text">${escapeHtml(
      getVoucherCompanyName(vouchers),
    )}</span></div>` +
    // 레거시 조회 화면(appr_view_forms_eas_voucher)이 정의한 토글 함수를 부름
    `<button type="button" class="btn btn-primary" id="btnAllVoucherDetails" onclick="toggleAllVoucherDetails()">${localize(
      '전표 상세 내역 보기',
    )}</button>` +
    '</div>' +
    '<div class="form-group" style="overflow-x:scroll;">' +
    '<table id="easTable" class="table">' +
    `<thead><tr>${head}</tr></thead>` +
    `<tbody id="easTableTbody">${rows}</tbody>` +
    `<tfoot><tr><th colspan="4" class="text-center">${localize('합계')}</th>` +
    `<td colspan="8" class="text-left">${escapeHtml(zSum)}</td></tr></tfoot>` +
    '</table></div></div>'
  );
};

/** 저장(POST/PUT) attributes — 전표 미확정이면 null(서버가 필수값으로 안내) */
export const buildSaVoucherAttributes = (
  snapshot: SaVoucherSnapshot | null | undefined,
): EasVoucherAttributes | null =>
  snapshot
    ? {
        sapDocumentNo: snapshot.sapDocumentNo,
        // 서버가 SAP 값으로 덮어씀. SAP 합계가 비면 '' 로 보내 서버 필수값 422 안내에 맡김
        zSum: snapshot.zSum ?? '',
        content: buildSaVoucherContentHtml(snapshot),
      }
    : null;

/**
 * SAP 전표 조회 화면을 새 창으로 — 레거시처럼 쿼리를 POST 폼 필드로 옮겨 보냄.
 * http(s) 가 아닌 링크는 열지 않음
 */
export const openSapVoucherLink = (link: string | null | undefined) => {
  if (!link || typeof document === 'undefined') return;
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return;
  const form = document.createElement('form');
  form.method = 'POST';
  form.target = '_blank';
  // 열린 SAP 창이 window.opener 로 포탈 창을 조작하지 못하게
  form.setAttribute('rel', 'noopener');
  form.action = `${url.origin}${url.pathname}`;
  form.style.display = 'none';
  url.searchParams.forEach((value, name) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};
